import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, ChevronLeft, ChevronRight, MessageCircle } from "lucide-react";
import { useLang } from "@/context/LanguageContext";

const SLIDES = [
  { img: "https://images.pexels.com/photos/8818732/pexels-photo-8818732.jpeg?auto=compress&cs=tinysrgb&w=1920", alt: "Traditional Maharashtrian festive thali spread" },
  { img: "https://images.unsplash.com/photo-1680993032090-1ef7ea9b51e5?w=1920&q=80&auto=format&fit=crop", alt: "Silver Maharashtrian thali with rice and curries" },
  { img: "https://images.pexels.com/photos/17223837/pexels-photo-17223837.jpeg?auto=compress&cs=tinysrgb&w=1920", alt: "Misal pav thali, Maharashtrian street food" },
  { img: "https://images.unsplash.com/photo-1559561724-732dbca7be1e?w=1920&q=80&auto=format&fit=crop", alt: "Variety of traditional Indian thali dishes" },
  { img: "https://images.pexels.com/photos/36885725/pexels-photo-36885725.jpeg?auto=compress&cs=tinysrgb&w=1920", alt: "Colourful Maharashtrian thali with rice and sides" },
];

export default function HeroSlider() {
  const { t } = useLang();
  const [i, setI] = useState(0);
  const go = useCallback((n) => setI((p) => (n + SLIDES.length) % SLIDES.length), []);
  const whatsappUrl = "https://wa.me/919923385867?text=Hello%20Mayuresh%20Caterers%2C%20I%20would%20like%20to%20enquire%20about%20catering%20for%20my%20event.";

  useEffect(() => {
    const timer = setInterval(() => setI((p) => (p + 1) % SLIDES.length), 6000);
    return () => clearInterval(timer);
  }, []);

  return (
    <section className="relative h-screen -mt-[72px] overflow-hidden" data-testid="hero-slider">
      <AnimatePresence initial={false}>
        <motion.img
          key={i}
          src={SLIDES[i].img}
          alt={SLIDES[i].alt}
          initial={{ opacity: 0, scale: 1.12 }}
          animate={{ opacity: 1, scale: 1.04 }}
          exit={{ opacity: 0 }}
          transition={{ opacity: { duration: 1.1 }, scale: { duration: 6.5, ease: "linear" } }}
          className="absolute inset-0 w-full h-full object-cover"
        />
      </AnimatePresence>
      <div className="absolute inset-0 hero-vignette" />

      <div className="relative h-full max-w-7xl mx-auto px-5 flex flex-col justify-center pt-16">
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="max-w-3xl">
          <div className="inline-flex items-center gap-3 border border-[#C9A66B]/50 bg-black/25 backdrop-blur-sm px-4 py-2 rounded-full mb-7">
            <span className="w-1.5 h-1.5 rounded-full bg-[#C9A66B] floaty" />
            <span className="label-tag text-[#E4CE9E] text-[0.62rem]">{t("heroBadge")}</span>
          </div>
          <h1 className="text-white display-title text-[3.2rem] sm:text-7xl lg:text-[5.4rem] font-medium"><span className="italic text-gold-grad">{t("heroTitle")}</span></h1>
          <p className="text-white/85 text-lg sm:text-xl mt-8 max-w-xl leading-relaxed font-light">{t("heroSub")}</p>
          <div className="flex flex-wrap gap-4 mt-10">
            <Link to="/build" className="btn-gold px-8 py-4 rounded-sm flex items-center gap-2 text-sm" data-testid="hero-build-cta">{t("buildCta")} <ArrowRight size={18} /></Link>
            <Link to="/build" className="btn-ghost-light px-8 py-4 rounded-sm text-sm" data-testid="hero-quote-cta">{t("quote")}</Link>
            <a href={whatsappUrl} target="_blank" rel="noreferrer" className="btn-ghost-light px-6 py-4 rounded-sm text-sm flex items-center gap-2" data-testid="hero-whatsapp-cta"><MessageCircle size={17} /> WhatsApp</a>
          </div>
          <p className="text-white/60 text-xs sm:text-sm mt-5">Roha, Raigad · +91 99233 85867</p>
        </motion.div>

        <div className="mt-12 sm:mt-16 grid grid-cols-3 max-w-xl gap-4 sm:gap-6 border-t border-white/15 pt-8">
          {[["15+", "years"], ["500+", "events"], ["1L+", "guests"]].map(([n, l]) => <div key={l}><p className="serif text-4xl sm:text-5xl text-[#C9A66B]">{n}</p><p className="text-white/60 text-xs sm:text-sm mt-1">{t(l)}</p></div>)}
        </div>
      </div>

      <button onClick={() => go(i - 1)} data-testid="hero-prev" aria-label="Previous slide" className="absolute left-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full border border-white/40 text-white flex items-center justify-center hover:bg-white hover:text-[#1A1A1A] transition-colors backdrop-blur-sm"><ChevronLeft size={20} /></button>
      <button onClick={() => go(i + 1)} data-testid="hero-next" aria-label="Next slide" className="absolute right-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full border border-white/40 text-white flex items-center justify-center hover:bg-white hover:text-[#1A1A1A] transition-colors backdrop-blur-sm"><ChevronRight size={20} /></button>
      <div className="absolute bottom-7 left-1/2 -translate-x-1/2 flex gap-2.5">{SLIDES.map((_, idx) => <button key={idx} onClick={() => setI(idx)} aria-label={`Go to slide ${idx + 1}`} className={`h-1.5 rounded-full transition-all ${idx === i ? "w-8 bg-[#C9A66B]" : "w-3 bg-white/40 hover:bg-white/70"}`} />)}</div>
    </section>
  );
}
